import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Message } from 'src/app/core/class/message';
import { CommonService } from 'src/app/core/services/common.service';
import { DocumentMohaService } from 'src/app/core/services/document-moha.service';
import { WarningComponent } from 'src/app/shared/components/warning/warning.component';
import { LinkDocumentAppendixDialogComponent } from '../link-document-appendix-dialog/link-document-appendix-dialog.component';
import { MultiDownloadService } from 'src/app/core/services/multi-download.service';
import { EnumDownloadMenuName } from 'src/app/core/constants/common';

export type LaborDocumentStatus = 'completed' | 'pending' | 'processing' | 'failed';

export interface LaborProfileWorker {
  id: string;
  name: string;
  certificateNo: string;
  avatarUrl?: string;
}

export interface LaborContractAppendix {
  id: string | number;
  name: string;
  mohaUuid?: string;
  documentCode: string;
  updatedTime: string;
  status: LaborDocumentStatus;
  statusCode?: number;
  statusLabel?: string;
  source?: LaborContractAppendixApi;
}

export interface LaborContract {
  id: string | number;
  mohaUuid?: string;
  name: string;
  documentCode: string;
  duration: string;
  status: LaborDocumentStatus;
  statusCode?: number;
  statusLabel?: string;
  ministryBadge?: string;
  expanded?: boolean;
  appendices: LaborContractAppendix[];
  source?: LaborContractInfoApi;
}

export interface LaborProfileViewModel {
  worker: LaborProfileWorker;
  contracts: LaborContract[];
}

export interface LaborProfilePersonalInfoApi {
  fullName: string;
  certificateNo: string;
}

export interface LaborContractAppendixApi {
  contractId: string;
  contractName: string;
  contractCode: string;
  statusAppendix: number;
  updatedTimeAppendix: string;
  isCheckStandardized?: boolean;
}

export interface LaborContractInfoApi {
  contractId: string;
  contractName: string;
  contractCode: string;
  mohaUuid: string;
  effectiveDate: string;
  expiredDate: string;
  status: number;
  updatedTime: string;
  contractAppendixInfoDTOS: LaborContractAppendixApi[];
}

export interface LaborProfileApiResponse {
  personalInfoDTO: LaborProfilePersonalInfoApi;
  contractInfoDTO: LaborContractInfoApi[];
  id: string;
}

export type LaborProfileDialogData = LaborProfileApiResponse | LaborProfileViewModel;

export type LaborProfileActionType =
  | 'send-all'
  | 'view-contract'
  | 'download-contract'
  | 'send-contract'
  | 'link-appendix'
  | 'view-appendix'
  | 'delete-appendix';

export interface LaborProfileAction {
  type: LaborProfileActionType;
  worker: LaborProfileWorker;
  contract?: LaborContract;
  appendix?: LaborContractAppendix;
}

@Component({
  selector: 'app-labor-profile-dialog',
  templateUrl: './labor-profile-dialog.component.html',
  styleUrls: ['./labor-profile-dialog.component.scss']
})
export class LaborProfileDialogComponent {
  @Output() action = new EventEmitter<LaborProfileAction>();

  profile: LaborProfileViewModel;
  id: string = '';
  readonly defaultStatusLabels: { [key in LaborDocumentStatus]: string } = {
    completed: this.message.getMessage('record_management_moha.status.sent'),
    pending: this.message.getMessage('record_management_moha.status.not_sent'),
    processing: this.message.getMessage('record_management_moha.status.processing'),
    failed: this.message.getMessage('record_management_moha.status.failed')
  };

  constructor(
    public dialogRef: MatDialogRef<LaborProfileDialogComponent>,
    private router: Router,
    private documentMohaService: DocumentMohaService,
    private dialog: MatDialog,
    public message: Message,
    private toastr: ToastrService,
    public commonService: CommonService,
    private downloadService: MultiDownloadService,
    @Inject(MAT_DIALOG_DATA) public data: LaborProfileDialogData
  ) {
    dialogRef.disableClose = false;
    let dataDialog: any = data;
    this.id = dataDialog.id;
    this.profile = this.isApiResponse(data) ? this.mapApiResponse(data) : data;
  }

  close(): void {
    this.dialogRef.close();
  }

  requestAction(
    type: LaborProfileActionType,
    contract?: LaborContract,
    appendix?: LaborContractAppendix
  ): void {
    this.action.emit({
      type,
      worker: this.profile.worker,
      contract,
      appendix
    });
  }

  getStatusLabel(status: LaborDocumentStatus, statusLabel?: string): string {
    return statusLabel || this.defaultStatusLabels[status];
  }

  trackContract(index: number, contract: LaborContract): string | number {
    return contract.id;
  }

  trackAppendix(index: number, appendix: LaborContractAppendix): string | number {
    return appendix.id + ':' + index;
  }

  toggleContract(contract: LaborContract): void {
    if (!this.hasAppendices(contract)) {
      return;
    }

    contract.expanded = !contract.expanded;
  }

  isContractExpanded(contract: LaborContract): boolean {
    return contract.expanded !== false;
  }

  hasAppendices(contract: LaborContract): boolean {
    return !!contract.appendices && contract.appendices.length > 0;
  }

  private isApiResponse(data: LaborProfileDialogData): data is LaborProfileApiResponse {
    return !!data && 'personalInfoDTO' in data;
  }

  private mapApiResponse(response: LaborProfileApiResponse): LaborProfileViewModel {
    const personalInfo = response.personalInfoDTO || ({} as LaborProfilePersonalInfoApi);
    const contracts = response.contractInfoDTO || [];

    return {
      worker: {
        id: personalInfo.certificateNo,
        name: personalInfo.fullName,
        certificateNo: personalInfo.certificateNo
      },
      contracts: contracts.map(contract => ({
        id: contract.contractId,
        name: contract.contractName,
        documentCode: contract.contractCode,
        mohaUuid: contract.mohaUuid,
        duration: this.formatDuration(contract.effectiveDate, contract.expiredDate),
        status: this.mapStatus(contract.status),
        statusCode: contract.status,
        expanded: true,
        appendices: (contract.contractAppendixInfoDTOS || []).map((appendix, index) => ({
          id: appendix.contractId,
          name: appendix.contractName,
          documentCode: appendix.contractCode,
          updatedTime: this.formatDateTime(appendix.updatedTimeAppendix),
          status: this.mapStatus(appendix.statusAppendix),
          statusCode: appendix.statusAppendix,
          source: appendix,
          isCheckStandardized: appendix.isCheckStandardized
        })),
        source: contract
      }))
    };
  }

  private mapStatus(status: number): LaborDocumentStatus {
    switch (status) {
      case 2:
        return 'processing';
      case 3:
        return 'failed';
      case 4:
        return 'completed';
      case 1:
        return 'pending';
      default:
        return 'pending';
    }
  }

  private formatDuration(effectiveDate: string, expiredDate: string): string {
    const from = this.formatDate(effectiveDate);
    const to = expiredDate ? this.formatDate(expiredDate) : this.message.getMessage('record_management_moha.indefinite_term');
    return [from, to].filter(Boolean).join(' - ');
  }

  private formatDateTime(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return this.pad(date.getDate()) + '/' +
      this.pad(date.getMonth() + 1) + '/' +
      date.getFullYear();
      // this.pad(date.getHours()) + ':' +
      // this.pad(date.getMinutes());
  }

  private formatDate(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return this.pad(date.getDate()) + '/' +
      this.pad(date.getMonth() + 1) + '/' +
      date.getFullYear();
  }

  private pad(value: number): string {
    return value < 10 ? '0' + value : value.toString();
  }

  viewDetail(contract: any, appendix?: any) {
    let id = '';
    if (appendix) {
      id = appendix.id
    } else {
      id = contract.id
    }
    this.toNavigate('/home-enterprise/view-document-moha/' + id, { goFrom: 'labor_profile' });
  }

  toNavigate(route, params = {}) {
    let queryParams: any = {
      ...params,
      routeLink: '/home-enterprise/record-management-moha/',
    };
    const url = this.router.serializeUrl(this.router.createUrlTree([route], { queryParams }));
    window.open(url, '_blank');
  }


  deleteAppendix(contract: any, appendix: any) {
    let message = this.commonService.format(this.message.getMessage('messages.delete_file_appendix'), appendix.name, contract.name);
    const dialog = this.dialog.open(WarningComponent, {
      width: '40%',
      data: message
    });
    dialog.afterClosed().subscribe(ref => {
      if (ref) {
        this.handleDelete(appendix.id);
      }
    });
  }

  handleDelete(id: string) {
    this.documentMohaService.deleteAppendix(id).subscribe(res => {
      if (res.success) {
        this.toastr.success(this.message.getMessage('messages.delete_success'));
        this.getPersonalContract();
      } else {
        this.toastr.error(res.message);
      }
    })
  }

  getPersonalContract() {
    this.documentMohaService.getPersonalContract(this.id).subscribe(res => {
      if (res.success) {
        this.profile = this.isApiResponse(res.data) ? this.mapApiResponse(res.data) : res.data;
        if (this.data && typeof this.data === 'object' && res.data) {
          this.data = { ...this.data, ...res.data };
        }
      } else {
        this.toastr.error(res.message)
      }
    });
  }

  downloadContract(contract: any) {
    if (!contract || !contract.id) {
      return;
    }
    this.downloadService.setData({
      menuContract: EnumDownloadMenuName.LABOR_CONTRACT,
      laborContractName: contract.name || '',
      isLaborContractDownload: true
    });
    this.downloadService.setOpen(true);
    this.documentMohaService.getKeyDownloadContractByZip(contract.id).subscribe(
      res => {
        if (res) {
          if (res.success === false || res.status === false || (res.code && res.code != '200' && res.code != 200)) {
            this.downloadService.setOpen(false);
            this.toastr.error(res.message || res.error || 'Tải file không thành công');
            return;
          }

          const downloadKey = typeof res === 'string' ? res : (res.key || res.data);
          if (!downloadKey || typeof downloadKey !== 'string') {
            this.downloadService.setOpen(false);
            this.toastr.error(res.message || 'Không thể lấy mã tải file');
            return;
          }

          const body = {
            key: downloadKey,
            token: sessionStorage.getItem('token'),
            lang: localStorage.getItem('__language')
          };

          this.documentMohaService.downloadFileZipBHS(body).subscribe(
            rs => {
              this.downloadService.setOpen(false);
              if (rs && rs.body) {
                if (rs.body.type === 'application/json') {
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const errorRes = JSON.parse(reader.result as string);
                      this.toastr.error(errorRes.message || 'Lỗi tải file');
                    } catch (e) {
                      this.toastr.error('Lỗi tải file');
                    }
                  };
                  reader.readAsText(rs.body);
                  return;
                }

                const blob = rs.body;
                const disposition = rs.headers.get('content-disposition');
                const fileName = this.getFileName(disposition) || 'download.zip';

                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                  window.navigator.msSaveOrOpenBlob(blob, fileName);
                  return;
                }

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');

                anchor.href = url;
                anchor.download = fileName;
                anchor.style.display = 'none';

                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();

                URL.revokeObjectURL(url);
              }
            },
            err => {
              this.downloadService.setOpen(false);
              this.toastr.error(err && err.message ? err.message : 'Error downloading file');
            }
          );
        } else {
          this.downloadService.setOpen(false);
          this.toastr.error('Tải file không thành công');
        }
      },
      err => {
        this.downloadService.setOpen(false);
        this.toastr.error(err && err.message ? err.message : 'Error fetching download key');
      }
    );
  }

  private getFileName(disposition: string): string {
    if (!disposition) {
      return 'download.zip';
    }

    const utf8Match = /filename\\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8Match) {
      return decodeURIComponent(utf8Match[1]);
    }

    const normalMatch = /filename=\"?([^\";]+)\"?/i.exec(disposition);
    return normalMatch ? normalMatch[1] : 'download.zip';
  }

  assignAppendix(contract: any) {

    const data = {
      contractOriginalId: contract.id,
      employeeEnterpriseId: this.id,
      ...this.profile
    }
    const dialogRe = this.dialog.open(LinkDocumentAppendixDialogComponent, {
      width: '900px',
      data: data,
    })
    dialogRe.afterClosed().subscribe(rs => {
      if (rs) {
        this.getPersonalContract();
      }
    });
  }
}
